import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { transportProviderAPI } from '../../services/api';
import { RIDE_ORDER_STATUS_LABELS as STATUS_LABELS } from './RideOrdersPage';

const EVENT_LABELS = {
  created: 'تم قبول العرض', driver_en_route: 'في الطريق للعميل', arrived: 'وصل السائق',
  in_progress: 'بدأ المشوار', completed: 'انتهى المشوار',
};

// One button per linear step — no OTP gate (see RideOrder.js's header
// comment: the passenger is present throughout, unlike a cargo handoff).
const NEXT_STEP = {
  accepted: { action: 'markRideEnRoute', label: 'انطلقت نحو العميل' },
  driver_en_route: { action: 'markRideArrived', label: 'وصلت لموقع العميل' },
  arrived: { action: 'markRideInProgress', label: 'بدأ المشوار' },
  in_progress: { action: 'markRideCompleted', label: 'إنهاء المشوار' },
};

const RideProviderOrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  // silent=true for background polls — only the very first load should show
  // the full-page spinner.
  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    transportProviderAPI.getRideOrderById(id)
      .then((res) => setOrder(res.data.data))
      .catch(() => toast.error('تعذّر تحميل الطلب'))
      .finally(() => { if (!silent) setLoading(false); });
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let interval = setInterval(() => load(true), 10000);
    const handleVisibility = () => {
      if (document.hidden) {
        clearInterval(interval);
      } else {
        load(true);
        interval = setInterval(() => load(true), 10000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [load]);

  const handleNextStep = async () => {
    const step = NEXT_STEP[order.status];
    if (!step) return;
    setActing(true);
    try {
      await transportProviderAPI[step.action](id);
      toast.success('تم تحديث الحالة');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'تعذّر التحديث');
    } finally { setActing(false); }
  };

  if (loading) return <div dir="rtl" className="p-6 text-center text-gray-400">جاري التحميل...</div>;
  if (!order) return <div dir="rtl" className="p-6 text-center text-gray-400">الطلب غير موجود</div>;

  const req = order.request || {};
  const step = NEXT_STEP[order.status];

  return (
    <div dir="rtl" className="p-4 sm:p-6 max-w-3xl mx-auto">
      <button onClick={() => navigate('/transport-provider/rides/orders')} className="text-sm text-blue-700 hover:underline mb-4">← رجوع للمشاوير</button>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h1 className="font-black text-gray-800">{order.orderNumber}</h1>
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_LABELS[order.status]?.color || 'bg-gray-100 text-gray-600'}`}>
            {STATUS_LABELS[order.status]?.label || order.status}
          </span>
        </div>
        <p className="text-sm text-gray-700 font-semibold mb-3">{req.fromLabel} ← {req.toLabel}</p>
        {req.customer && (
          <div className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-xl px-3 py-2.5 mb-3">
            <div>
              <p className="text-xs text-blue-600 font-semibold">العميل</p>
              <p className="text-sm font-bold text-gray-800">{req.customer.name}</p>
            </div>
            {req.customer.phone && (
              <a href={`tel:${req.customer.phone}`} className="text-sm font-bold text-blue-700 hover:underline" dir="ltr">
                📞 {req.customer.phone}
              </a>
            )}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-gray-50 rounded-xl p-3">
          <div><p className="text-xs text-gray-500">السعر</p><p className="font-bold text-gray-800">{order.price} ج.س</p></div>
          <div><p className="text-xs text-gray-500">عمولة سافر</p><p className="font-bold text-red-600">{order.commissionAmount} ج.س</p></div>
          <div><p className="text-xs text-gray-500">صافي المستحق</p><p className="font-bold text-emerald-700">{order.providerReceivable} ج.س</p></div>
        </div>
        <p className="text-xs text-gray-400 mt-2">💵 يُحصَّل نقداً من العميل مباشرة</p>
      </div>

      {step ? (
        <button onClick={handleNextStep} disabled={acting}
          className="w-full bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white py-3 rounded-xl font-bold transition">
          {acting ? '...جارٍ التحديث' : step.label}
        </button>
      ) : order.status === 'completed' ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-sm text-emerald-800 text-center font-bold">
          ✅ تم إنهاء المشوار
        </div>
      ) : null}

      {order.events?.length > 0 && (
        <div className="mt-6">
          <p className="text-xs font-bold text-gray-600 mb-2">سجل الطلب</p>
          <div className="space-y-2">
            {order.events.map((ev) => (
              <div key={ev.id} className="flex items-center gap-3 text-xs">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                <span className="text-gray-700 font-semibold">{EVENT_LABELS[ev.event] || ev.event}</span>
                <span className="text-gray-400">{new Date(ev.createdAt).toLocaleString('ar-SA')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RideProviderOrderDetailPage;
